import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EditPageLayout } from '@/components/shared/EditPageLayout';
import { FormSection } from '@/components/shared/FormSection';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { ProductStatusBadge } from '@/components/shared/ProductStatusBadge';
import { TableCard } from '@/components/shared/TableCard';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { getItemDetectionStatus } from '@/utils/detectionStatus';
import type {
  Category,
  CustomFieldDefinition,
  CustomFieldType,
  Item,
  Branch,
  Shelf,
} from '@packages/shared-types';
import {
  getCategoryById,
  type UpdateCategoryPayload,
  updateCategory,
  deleteCategory,
} from '@/services/categoryService';
import {
  listCustomFieldDefinitions,
  createCustomFieldDefinition,
  updateCustomFieldDefinition,
  deleteCustomFieldDefinition,
  type CreateCustomFieldDefinitionPayload,
} from '@/services/customFieldService';
import { listItems } from '@/services/itemService';
import { listBranches } from '@/services/branchService';
import { listShelves } from '@/services/shelfService';

const categoryFormSchema = z.object({
  name: z.string().min(1, 'pages.categories.form.nameRequired'),
  code: z
    .string()
    .optional()
    .refine((v) => !v || /^[0-9A-Za-z]{3}$/.test(v), {
      message: 'pages.categories.form.codeInvalid',
    }),
});

const customFieldFormSchema = z.object({
  name: z.string().min(1, 'pages.customFields.form.nameRequired'),
  key: z.string().min(1, 'pages.customFields.form.keyRequired'),
  type: z.enum(['text', 'textarea', 'number', 'url', 'options']),
  required: z.boolean().optional(),
  options: z.string().optional(),
});

type CategoryFormValues = z.infer<typeof categoryFormSchema>;
type CustomFieldFormValues = z.infer<typeof customFieldFormSchema>;

export function EditCategoryPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editTitle = useIndustryTranslation('pages.categories.edit.title');
  const editDescription = useIndustryTranslation('pages.categories.edit.description');
  const deleteDialogTitle = useIndustryTranslation('pages.categories.dialogs.delete.title');
  const loadingLabel = useIndustryTranslation('pages.categories.list.loading');
  const successUpdate = useIndustryTranslation('pages.categories.success.update');
  const successDelete = useIndustryTranslation('pages.categories.success.delete');
  const errorLoad = useIndustryTranslation('pages.categories.error.load');
  const errorUpdate = useIndustryTranslation('pages.categories.error.update');
  const errorDelete = useIndustryTranslation('pages.categories.error.delete');
  const errorNotFound = useIndustryTranslation('pages.categories.error.notFound');
  const itemsSectionLabel = useIndustryTranslation('pages.categories.details.itemsSection');
  const noItemsLabel = useIndustryTranslation('pages.categories.details.noItems');
  const nameTableLabel = useIndustryTranslation('pages.items.table.name');
  const skuTableLabel = useIndustryTranslation('pages.items.table.sku');
  const branchTableLabel = useIndustryTranslation('pages.items.table.branch');
  const shelfTableLabel = useIndustryTranslation('pages.items.table.shelf');
  const statusTableLabel = useIndustryTranslation('pages.items.table.status');
  const statusDetectedLabel = useIndustryTranslation('pages.items.status.detected');
  const statusNotSeenRecentlyLabel = useIndustryTranslation(
    'pages.items.status.notSeenRecently',
  );
  const statusMissingLabel = useIndustryTranslation('pages.items.status.missing');

  const [category, setCategory] = useState<Category | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<
    CustomFieldDefinition[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isLoadingDefinitions, setIsLoadingDefinitions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCustomFieldDialogOpen, setIsCustomFieldDialogOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] =
    useState<CustomFieldDefinition | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: '',
      code: '',
    },
  });

  const {
    register: registerCf,
    handleSubmit: handleSubmitCf,
    reset: resetCf,
    watch: watchCf,
    setValue: setValueCf,
    formState: { errors: cfErrors, isSubmitting: isCfSubmitting },
  } = useForm<CustomFieldFormValues>({
    resolver: zodResolver(customFieldFormSchema),
    defaultValues: {
      name: '',
      key: '',
      type: 'text',
      required: false,
      options: '',
    },
  });

  const watchCfType = watchCf('type');

  const formatErrorMessage = useCallback(
    (message?: string): string | null => {
      if (!message) {
        return null;
      }

      return t(message);
    },
    [t],
  );

  const loadCategory = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getCategoryById(id);
      setCategory(data);
      reset({
        name: data.name,
        code: data.code ?? '',
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : errorLoad,
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, reset, errorLoad]);

  const loadItems = useCallback(async () => {
    if (!id) return;
    setIsLoadingItems(true);
    try {
      const [itemsData, branchesData, shelvesData] = await Promise.all([
        listItems(undefined, id),
        listBranches(),
        listShelves(),
      ]);
      setItems(itemsData);
      setBranches(branchesData);
      setShelves(shelvesData);
    } catch (err) {
      console.error('Failed to load items', err);
    } finally {
      setIsLoadingItems(false);
    }
  }, [id]);

  const loadDefinitions = useCallback(async () => {
    if (!id) return;
    setIsLoadingDefinitions(true);
    try {
      const defs = await listCustomFieldDefinitions(id);
      setCustomFieldDefinitions(defs);
    } catch (err) {
      console.error('Failed to load custom field definitions', err);
    } finally {
      setIsLoadingDefinitions(false);
    }
  }, [id]);

  const handleAddCustomField = (): void => {
    setEditingDefinition(null);
    resetCf({
      name: '',
      key: '',
      type: 'text',
      required: false,
      options: '',
    });
    setIsCustomFieldDialogOpen(true);
  };

  const handleEditCustomField = (def: CustomFieldDefinition): void => {
    setEditingDefinition(def);
    resetCf({
      name: def.name,
      key: def.key,
      type: def.type,
      required: def.required,
      options: def.options?.join(', ') ?? '',
    });
    setIsCustomFieldDialogOpen(true);
  };

  const handleSubmitCustomField = async (
    values: CustomFieldFormValues,
  ): Promise<void> => {
    if (!id) return;
    const options =
      values.type === 'options' && values.options
        ? values.options.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined;
    try {
      if (editingDefinition) {
        await updateCustomFieldDefinition(editingDefinition.id, {
          name: values.name,
          key: values.key,
          type: values.type as CustomFieldType,
          required: values.required ?? false,
          options,
        });
        toast.success(t('pages.customFields.success.update'));
      } else {
        await createCustomFieldDefinition({
          name: values.name,
          key: values.key,
          type: values.type as CustomFieldType,
          required: values.required ?? false,
          options,
          categoryId: id,
        } as CreateCustomFieldDefinitionPayload);
        toast.success(t('pages.customFields.success.create'));
      }
      setIsCustomFieldDialogOpen(false);
      setEditingDefinition(null);
      void loadDefinitions();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('pages.customFields.error.save'),
      );
    }
  };

  const handleDeleteCustomField = async (def: CustomFieldDefinition): Promise<void> => {
    if (!window.confirm(t('pages.customFields.dialogs.delete.confirm', { name: def.name }))) return;
    try {
      await deleteCustomFieldDefinition(def.id);
      toast.success(t('pages.customFields.success.delete'));
      void loadDefinitions();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('pages.customFields.error.delete'),
      );
    }
  };

  useEffect(() => {
    void loadCategory();
  }, [loadCategory]);

  useEffect(() => {
    if (category) {
      void loadItems();
      void loadDefinitions();
    }
  }, [category, loadItems, loadDefinitions]);

  const handleSubmitCategory = async (
    values: CategoryFormValues,
  ): Promise<void> => {
    if (!id) return;
    setIsSaving(true);
    setError(null);

    const payload: UpdateCategoryPayload = {
      name: values.name,
      code: values.code?.trim() || undefined,
    };

    try {
      await updateCategory(id, payload);
      await loadCategory();
      toast.success(successUpdate);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorUpdate;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id || !category) return;
    setIsDeleting(true);
    try {
      await deleteCategory(id);
      toast.success(successDelete);
      navigate('/categories');
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorDelete;
      setError(errorMessage);
      toast.error(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSubmit(handleSubmitCategory)();
  };

  if (!id) {
    return (
      <div className="p-6 text-sm text-destructive">
        {errorNotFound}
      </div>
    );
  }

  return (
    <EditPageLayout
      title={editTitle}
      description={category ? editDescription : undefined}
      isLoading={isLoading}
      error={error}
      formId="category-edit-form"
      onSubmit={handleFormSubmit}
      onCancel={() => navigate(-1)}
      cancelLabel={t('pages.categories.actions.cancel')}
      onSave={() => void handleSubmit(handleSubmitCategory)()}
      saveLabel={t('pages.categories.actions.update')}
      savingLabel={t('pages.categories.actions.updating')}
      isSaving={isSaving}
      onDelete={handleDelete}
      deleteLabel={t('pages.categories.actions.delete')}
      deletingLabel={t('pages.categories.actions.deleting')}
      isDeleting={isDeleting}
      deleteDialogTitle={deleteDialogTitle}
      deleteDialogDescription={t('pages.categories.dialogs.delete.description', {
        name: category?.name,
      })}
      entityExists={!!category}
    >
      <form
        id="category-edit-form"
        className="space-y-6"
        onSubmit={handleSubmit(handleSubmitCategory)}
      >
        <FormSection
          title={t('pages.categories.form.code')}
          description={t('pages.categories.form.codeDescription')}
          error={
            errors.code
              ? formatErrorMessage(errors.code.message) ??
                (errors.code.message as string)
              : null
          }
        >
          <Input
            id="category-code"
            {...register('code')}
            placeholder={t('pages.categories.form.codePlaceholder')}
            disabled={isSaving}
            maxLength={3}
            className="font-mono"
          />
        </FormSection>
        <FormSection
          title={t('pages.categories.form.name')}
          description={t('pages.categories.form.name')}
          error={
            errors.name
              ? formatErrorMessage(errors.name.message) ??
                (errors.name.message as string)
              : null
          }
        >
          <Input
            id="category-name"
            {...register('name')}
            placeholder={t('pages.categories.form.name')}
            disabled={isSaving}
          />
        </FormSection>
      </form>

      {category && (
        <>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {t('pages.customFields.title')}
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddCustomField}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('pages.customFields.actions.add')}
              </Button>
            </div>
            {isLoadingDefinitions ? (
              <div className="flex items-center gap-2 py-4">
                <LoadingLogo size="sm" />
                <span className="text-sm text-muted-foreground">
                  {loadingLabel}
                </span>
              </div>
            ) : customFieldDefinitions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                {t('pages.customFields.empty')}
              </p>
            ) : (
              <TableCard>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('pages.customFields.table.name')}</TableHead>
                      <TableHead>{t('pages.customFields.table.key')}</TableHead>
                      <TableHead>{t('pages.customFields.table.type')}</TableHead>
                      <TableHead>{t('pages.customFields.table.required')}</TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customFieldDefinitions.map((def) => (
                      <TableRow key={def.id}>
                        <TableCell className="font-medium">{def.name}</TableCell>
                        <TableCell className="font-mono text-sm">{def.key}</TableCell>
                        <TableCell>{t(`pages.customFields.types.${def.type}`)}</TableCell>
                        <TableCell>{def.required ? t('pages.common.yes') : t('pages.common.no')}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEditCustomField(def)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => void handleDeleteCustomField(def)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableCard>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {itemsSectionLabel}
            </h3>
          <TableCard>
            {isLoadingItems ? (
              <div className="flex flex-col items-center gap-3 py-8">
                <LoadingLogo size="sm" />
                <span className="text-sm text-muted-foreground">{loadingLabel}</span>
              </div>
            ) : items.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {noItemsLabel}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{nameTableLabel}</TableHead>
                    <TableHead>{skuTableLabel}</TableHead>
                    <TableHead>{branchTableLabel}</TableHead>
                    <TableHead>{shelfTableLabel}</TableHead>
                    <TableHead>{statusTableLabel}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const branch = branches.find((b) => b.id === item.branchId);
                    const shelf = shelves.find((s) => s.id === item.currentShelfId);
                    const detectionStatus = getItemDetectionStatus(item);
                    const statusLabel =
                      detectionStatus === 'detected'
                        ? statusDetectedLabel
                        : detectionStatus === 'not_seen_recently'
                          ? statusNotSeenRecentlyLabel
                          : statusMissingLabel;
                    return (
                      <TableRow
                        key={item.id}
                        onRowClick={() => navigate(`/items/${item.id}`)}
                      >
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.sku}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {branch?.name || '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {shelf?.name || '—'}
                        </TableCell>
                        <TableCell>
                          <ProductStatusBadge
                            status={detectionStatus}
                            label={statusLabel}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableCard>
          </div>
        </>
      )}

      <Dialog
        open={isCustomFieldDialogOpen}
        onOpenChange={(open) => {
          setIsCustomFieldDialogOpen(open);
          if (!open) setEditingDefinition(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingDefinition
                ? t('pages.customFields.dialogs.edit.title')
                : t('pages.customFields.dialogs.create.title')}
            </DialogTitle>
            <DialogDescription>
              {editingDefinition
                ? t('pages.customFields.dialogs.edit.description')
                : t('pages.customFields.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCf(handleSubmitCustomField)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cf-name">{t('pages.customFields.form.name')} *</Label>
                <Input
                  id="cf-name"
                  {...registerCf('name', {
                    onChange: (e) => {
                      if (!editingDefinition) {
                        const key = e.target.value
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, '_');
                        setValueCf('key', key);
                      }
                    },
                  })}
                  placeholder={t('pages.customFields.form.namePlaceholder')}
                />
                {cfErrors.name && (
                  <p className="text-sm text-destructive">
                    {t(cfErrors.name.message ?? 'pages.customFields.form.nameRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-key">{t('pages.customFields.form.key')} *</Label>
                <Input
                  id="cf-key"
                  {...registerCf('key')}
                  placeholder={t('pages.customFields.form.keyPlaceholder')}
                  disabled={!!editingDefinition}
                />
                {cfErrors.key && (
                  <p className="text-sm text-destructive">
                    {t(cfErrors.key.message ?? 'pages.customFields.form.keyRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-type">{t('pages.customFields.form.type')} *</Label>
                <select
                  id="cf-type"
                  {...registerCf('type')}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="text">{t('pages.customFields.types.text')}</option>
                  <option value="textarea">{t('pages.customFields.types.textarea')}</option>
                  <option value="number">{t('pages.customFields.types.number')}</option>
                  <option value="url">{t('pages.customFields.types.url')}</option>
                  <option value="options">{t('pages.customFields.types.options')}</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="cf-required"
                  checked={watchCf('required')}
                  onCheckedChange={(checked) =>
                    setValueCf('required', checked === true)
                  }
                />
                <Label htmlFor="cf-required" className="cursor-pointer">
                  {t('pages.customFields.form.required')}
                </Label>
              </div>
              {watchCfType === 'options' && (
                <div className="space-y-2">
                  <Label htmlFor="cf-options">{t('pages.customFields.form.options')}</Label>
                  <Input
                    id="cf-options"
                    {...registerCf('options')}
                    placeholder={t('pages.customFields.form.optionsPlaceholder')}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCustomFieldDialogOpen(false);
                  setEditingDefinition(null);
                }}
              >
                {t('pages.categories.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isCfSubmitting}>
                {isCfSubmitting
                  ? t('pages.categories.actions.saving')
                  : editingDefinition
                    ? t('pages.customFields.actions.save')
                    : t('pages.customFields.actions.add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </EditPageLayout>
  );
}
